// absolute imports
import { useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// relative imports
import {
  currentAccountConfigVar,
  switchingAccountsVar,
} from '../../data/apollo/cache/reactiveVars';
import {
  getAppConfig,
  getAppConfigId,
  getRemoteAccountConfig,
  getRemoteAppConfig,
} from '../../data/app-config/configs/configUtilities';
import { useAccount } from './AccountProvider';

/* ENVs related to App Config:
  REACT_APP_CONFIGURATION_SUBTYPE_ID -> determines app style configuration
*/

export const AccountConfigContext = createContext();
export const useAccountConfig = () => {
  const context = useContext(AccountConfigContext);
  // Default object if context is undefined
  // Note: Providers always render, but provides safety during possible rendering race conditions (e.g. logout/cache clearing)
  if (!context) {
    return {
      accountConfig: {},
      updateAccountConfig: () => {},
      configError: null,
      loadingAccountConfig: true,
      clearConfigError: () => {},
    };
  }
  return context;
};

export const AccountConfigProvider = ({ children }) => {
  const accountData = useAccount();
  const currentAccount = accountData?.currentAccount;
  const { applicationType } = currentAccount || {};
  const accountConfig = useReactiveVar(currentAccountConfigVar);
  const [configError, setConfigError] = useState(null);
  const [loadingAccountConfig, setLoadingAccountConfig] = useState(false);
  const [loadingAppConfig, setLoadingAppConfig] = useState(false);

  const updateAccountConfig = useCallback(
    async (applicationTypeId) => {
      if (!applicationTypeId) return;

      setLoadingAccountConfig(true);
      setConfigError(null);

      try {
        const newAccountConfig = await getRemoteAccountConfig({
          id: applicationTypeId,
        });

        currentAccountConfigVar(newAccountConfig);

        // Ensure app config updates based on account config (if the app config ID has changed)
        try {
          const newAppConfigId = getAppConfigId(newAccountConfig);
          const { currentAppConfig } = getAppConfig();
          const currentAppConfigId = currentAppConfig?.id;

          if (newAppConfigId && newAppConfigId !== currentAppConfigId) {
            setLoadingAppConfig(true);
            await getRemoteAppConfig(newAppConfigId);
            setLoadingAppConfig(false);
          }
        } catch (error) {
          console.warn('Failed to update app config for account:', error);
          setLoadingAppConfig(false);
        }
      } catch (error) {
        console.error('Failed to load account configuration:', error);
        setConfigError({
          message: error.message,
          applicationTypeId: applicationTypeId,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setLoadingAccountConfig(false);
        // Clear switching state now that both account and app config have loaded
        if (switchingAccountsVar() && !loadingAppConfig) {
          switchingAccountsVar(false);
        }
      }
    },
    [loadingAppConfig],
  );

  useEffect(() => {
    if (applicationType?.id && applicationType.id !== accountConfig?.id) {
      updateAccountConfig(applicationType.id);
    }
  }, [applicationType?.id, accountConfig?.id, updateAccountConfig]);

  const accountConfigValue = useMemo(
    () => ({
      accountConfig: accountConfig || {},
      updateAccountConfig: () => updateAccountConfig(applicationType?.id),
      configError,
      loadingAccountConfig: loadingAccountConfig || loadingAppConfig,
      clearConfigError: () => {
        if (configError) setConfigError(null);
      },
    }),
    [
      accountConfig,
      configError,
      loadingAccountConfig,
      loadingAppConfig,
      applicationType?.id,
      updateAccountConfig,
    ],
  );

  return (
    <AccountConfigContext.Provider value={accountConfigValue}>
      {children}
    </AccountConfigContext.Provider>
  );
};

AccountConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
